/**
 * Iter31 FIX 6 — Plans management moved from Owner Console → Admin Paneli.
 *
 * Full CRUD for subscription plans: create (Shto Abonim të Ri), edit, hide/show,
 * delete. Owner Console → Vendet e Abonimeve keeps ONLY the capacity/threshold
 * configuration per plan; the catalogue itself lives here.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FloppyDisk, X as XIcon, Plus, Eye, EyeSlash, Trash } from '@phosphor-icons/react';
import { useConfirmDialog } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local toggle so we don't import from OwnerConsolePage.
function Toggle({ on, onToggle, testid }) {
  return (
    <button onClick={onToggle} data-testid={testid}
            className={`w-10 h-6 rounded-full flex items-center px-0.5 transition ${
              on ? 'bg-[#FFD700] justify-end' : 'bg-white/10 justify-start'
            }`}>
      <span className={`w-5 h-5 rounded-full block ${on ? 'bg-black' : 'bg-white/70'}`} />
    </button>
  );
}

export default function PlansManagerTab() {
  const { t } = useLanguage();
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [draft, setDraft] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, prRes] = await Promise.all([
        axios.get(`${API}/subscription-plans`, { withCredentials: true }),
        axios.get(`${API}/wash-programs`),
      ]);
      setPlans(pRes.data || []);
      setPrograms(prRes.data || []);
    } catch (e) { toast.error('Failed'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setDraft({
      name_sq: '', name_en: '',
      price: 2500, washes_included: 6,
      wash_program_id: programs?.[0]?.program_id || 'prog_standard',
      is_unlimited: false, cooldown_hours: 24,
      is_hidden: false, vip_earns_points: true,
    });
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setDraft({
      ...p,
      is_hidden: !!p.is_hidden,
      vip_earns_points: p.vip_earns_points !== false,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.name_sq || !draft.name_en) {
      toast.error('Emri i abonimit është i detyrueshëm');
      return;
    }
    const payload = {
      name_en: draft.name_en.trim(),
      name_sq: draft.name_sq.trim(),
      price: parseInt(draft.price) || 0,
      washes_included: draft.is_unlimited ? 0 : (parseInt(draft.washes_included) || 0),
      wash_program_id: draft.wash_program_id,
      is_unlimited: !!draft.is_unlimited,
      cooldown_hours: draft.is_unlimited ? (parseInt(draft.cooldown_hours) || 24) : null,
      is_hidden: !!draft.is_hidden,
      vip_earns_points: !!draft.vip_earns_points,
    };
    try {
      if (draft.plan_id) {
        await axios.put(`${API}/subscription-plans/${draft.plan_id}`, payload, { withCredentials: true });
        toast.success(t('ocPlanUpdated') || 'Ndryshuar');
      } else {
        await axios.post(`${API}/subscription-plans`, payload, { withCredentials: true });
        toast.success(t('ocPlanCreated') || 'Krijuar');
      }
      setShowDialog(false); setDraft(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const toggleHidden = async (p) => {
    try {
      const payload = {
        name_en: p.name_en, name_sq: p.name_sq,
        price: p.price, washes_included: p.washes_included || 0,
        wash_program_id: p.wash_program_id, is_unlimited: !!p.is_unlimited,
        cooldown_hours: p.cooldown_hours,
        is_hidden: !p.is_hidden,
        vip_earns_points: p.vip_earns_points !== false,
      };
      await axios.put(`${API}/subscription-plans/${p.plan_id}`, payload, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const remove = async (p) => {
    const ok = await confirm({
      title: t('ocPlanDelete') || 'Fshi abonimin',
      description: `${p.name_sq || p.name_en}`,
      confirmText: 'Fshi', cancelText: 'Anulo', danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/subscription-plans/${p.plan_id}`, { withCredentials: true });
      toast.success(t('ocPlanDeleted') || 'Fshirë');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="plans-manager-tab">
      <ConfirmDialog />

      <div className="flex items-center justify-between">
        <p className="text-xs text-white/55 uppercase tracking-wider">
          {t('plansTab')} ({plans.length})
        </p>
        <Button onClick={openNew}
                className="h-9 px-3 rounded-xl bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold text-xs"
                data-testid="plans-add-cta">
          <Plus size={14} weight="bold" className="mr-1" />
          {t('ocAddPlanCta') || 'Shto Abonim të Ri'}
        </Button>
      </div>

      <div className="space-y-2" data-testid="plans-list">
        {plans.map((p) => (
          <div key={p.plan_id} data-testid={`plan-row-${p.plan_id}`}
               className="p-3 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-white truncate">{p.name_sq || p.name_en}</p>
                {p.is_hidden && (
                  <span className="text-[9px] uppercase tracking-wider bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded"
                        data-testid={`plan-hidden-badge-${p.plan_id}`}>
                    {t('ocPlanHiddenBadge') || 'I fshehur'}
                  </span>
                )}
                {p.vip_earns_points !== false && (
                  <span className="text-[9px] uppercase tracking-wider bg-[#FFD700]/20 text-[#FFD700] px-1.5 py-0.5 rounded">
                    VIP
                  </span>
                )}
              </div>
              <p className="text-[10px] text-white/45">
                {p.price?.toLocaleString()} L · {p.is_unlimited ? '∞' : `${p.washes_included || 0}×`} · {p.wash_program_id?.replace('prog_', '').toUpperCase()}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => toggleHidden(p)}
                      className="h-8 w-8 text-white/60 hover:text-white"
                      data-testid={`plan-toggle-visible-${p.plan_id}`}>
                {p.is_hidden ? <EyeSlash size={14} /> : <Eye size={14} />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => openEdit(p)}
                      className="h-8 w-8 text-white/60 hover:text-white"
                      data-testid={`plan-edit-${p.plan_id}`}>
                <FloppyDisk size={14} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => remove(p)}
                      className="h-8 w-8 text-red-400/70 hover:text-red-400"
                      data-testid={`plan-delete-${p.plan_id}`}>
                <Trash size={14} />
              </Button>
            </div>
          </div>
        ))}
        {plans.length === 0 && <p className="text-xs text-white/40 text-center py-3">—</p>}
      </div>

      {showDialog && draft && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-end sm:items-center justify-center p-4"
             onClick={() => setShowDialog(false)}
             data-testid="plans-dialog-overlay">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-[#0a0a0a] border border-[#FFD700]/25 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
               data-testid="plans-dialog">
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/5 p-3 flex items-center justify-between z-10">
              <p className="text-sm font-bold text-white">
                {draft.plan_id ? (t('ocPlanEdit') || 'Ndrysho') : (t('ocAddPlanTitle') || 'Abonim i Ri')}
              </p>
              <button onClick={() => setShowDialog(false)}
                      className="text-white/40 hover:text-white"
                      data-testid="plans-dialog-close">
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1 block">{t('ocPlanFieldName')} (SQ)</label>
                <Input value={draft.name_sq}
                       onChange={(e) => setDraft({ ...draft, name_sq: e.target.value })}
                       placeholder="p.sh. Ekonomik Pass"
                       className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                       data-testid="plans-name-sq" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1 block">{t('ocPlanFieldNameEn')}</label>
                <Input value={draft.name_en}
                       onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
                       placeholder="e.g. Economic Pass"
                       className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                       data-testid="plans-name-en" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1 block">{t('ocPlanFieldPrice')}</label>
                  <Input type="number" value={draft.price}
                         onChange={(e) => setDraft({ ...draft, price: parseInt(e.target.value) || 0 })}
                         className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm tabular-nums"
                         data-testid="plans-price" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1 block">{t('ocPlanFieldWashes')}</label>
                  <Input type="number" value={draft.washes_included}
                         disabled={!!draft.is_unlimited}
                         onChange={(e) => setDraft({ ...draft, washes_included: parseInt(e.target.value) || 0 })}
                         className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm tabular-nums disabled:opacity-40"
                         data-testid="plans-washes" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/55 mb-1 block">{t('ocPlanFieldProgram')}</label>
                <select value={draft.wash_program_id}
                        onChange={(e) => setDraft({ ...draft, wash_program_id: e.target.value })}
                        className="w-full h-11 bg-[#050505] border border-white/10 rounded-xl text-sm text-white px-3"
                        data-testid="plans-program">
                  {programs.map((pg) => (
                    <option key={pg.program_id} value={pg.program_id}>{pg.name_en}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-xs text-white/70">{t('ocPlanFieldUnlimited')}</span>
                <Toggle on={!!draft.is_unlimited}
                        onToggle={() => setDraft({ ...draft, is_unlimited: !draft.is_unlimited })}
                        testid="plans-unlimited" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-xs text-white/70">{t('ocPlanFieldEarnsVip')}</span>
                <Toggle on={!!draft.vip_earns_points}
                        onToggle={() => setDraft({ ...draft, vip_earns_points: !draft.vip_earns_points })}
                        testid="plans-earns-vip" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                <span className="text-xs text-white/70">{t('ocPlanFieldHidden')}</span>
                <Toggle on={!!draft.is_hidden}
                        onToggle={() => setDraft({ ...draft, is_hidden: !draft.is_hidden })}
                        testid="plans-hidden" />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button onClick={() => setShowDialog(false)}
                        variant="outline"
                        className="h-11 rounded-xl border-white/10 bg-white/5 hover:bg-white/10 text-white text-sm"
                        data-testid="plans-cancel">
                  {t('cancel')}
                </Button>
                <Button onClick={save}
                        className="h-11 rounded-xl bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold text-sm"
                        data-testid="plans-save">
                  <FloppyDisk size={14} className="mr-1.5" weight="fill" />
                  {t('save') || 'Ruaj'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
